import React, { createContext, useContext, useEffect } from "react";
import posthog from "posthog-js";

const PostHogContext = createContext();

// Query params that must never reach PostHog. `code`/`state` are the Google
// OAuth round trip, `redirect_uri`/`state` are the RevenueCat funnel handoff —
// all of them land in the URL of pages we now capture pageviews for.
const SENSITIVE_QUERY_PARAMS = [
  'code',
  'state',
  'funnel_state',
  'redirect_uri',
  'funnel_redirect_uri',
  'access_token',
  'refresh_token',
  'id_token',
  'token',
];

// URL-valued properties PostHog attaches automatically
const URL_PROPERTIES = [
  '$current_url',
  '$initial_current_url',
  '$referrer',
  '$initial_referrer',
  '$session_entry_url',
];

const scrubUrl = (value) => {
  try {
    const url = new URL(value);
    let changed = false;
    SENSITIVE_QUERY_PARAMS.forEach((param) => {
      if (url.searchParams.has(param)) {
        url.searchParams.set(param, 'redacted');
        changed = true;
      }
    });
    return changed ? url.toString() : value;
  } catch (_) {
    // Not a parseable URL (e.g. "$direct") — leave it alone
    return value;
  }
};

export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) {
    throw new Error("usePostHog must be used within a PostHogProvider");
  }
  return context;
};

// Runs at import time, NOT from a useEffect. React flushes child effects before
// parent ones, so a provider that initialized inside its own effect was still
// uninitialized when the page below it fired its first event — and posthog-js
// drops captures made before init() rather than queueing them. That silently
// cost every page's first event, including the funnel's entry step.
let initialized = false;
const initPostHog = () => {
  if (initialized || typeof window === "undefined") return;

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  // Env-driven config so dev/prod can use different PostHog projects.
  const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
  const POSTHOG_HOST =
    process.env.REACT_APP_POSTHOG_HOST || "https://us.posthog.com";

  // Safety: by default, never fire from localhost — prevents polluting prod data
  // when a dev forgets to set up `.env.local`. To opt-in to local tracking
  // (against a DEV PostHog project), set REACT_APP_POSTHOG_ALLOW_LOCALHOST=true
  // in `.env.local` along with the dev project's REACT_APP_POSTHOG_KEY.
  const allowOnLocalhost =
    process.env.REACT_APP_POSTHOG_ALLOW_LOCALHOST === "true";
  const shouldInit = !!POSTHOG_KEY && (!isLocalhost || allowOnLocalhost);

  if (!shouldInit) {
    // Loud warning so devs notice if they thought tracking was on.
    // eslint-disable-next-line no-console
    console.info(
      "[PostHog] disabled —",
      !POSTHOG_KEY
        ? "no REACT_APP_POSTHOG_KEY set"
        : "localhost (set REACT_APP_POSTHOG_ALLOW_LOCALHOST=true to enable)"
    );
    return;
  }

  initialized = true;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Capture $pageview on every SPA route change, not just the first load.
    // Without this, pages only appeared in PostHog if someone remembered to
    // call usePageTracking() in the component — so most of the app, and the
    // whole /funnel-auth flow, was invisible. The custom `page_viewed` events
    // still fire alongside; they carry friendly page names.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    // Strip OAuth codes / funnel handoff tokens out of captured URLs
    sanitize_properties: (properties) => {
      URL_PROPERTIES.forEach((key) => {
        if (typeof properties[key] === 'string') {
          properties[key] = scrubUrl(properties[key]);
        }
      });
      return properties;
    },
    // Verbose console logging for every captured event in non-prod builds.
    // Makes it trivial to verify implementation while running test scripts locally.
    debug: process.env.NODE_ENV !== "production",
    // Session recording — never on localhost (avoids recording dev work)
    session_recording: {
      enabled: !isLocalhost,
    },
    // Person profiles for better user identification
    person_profiles: "always",
  });
};

initPostHog();

export const PostHogProvider = ({ children }) => {
  // Init already ran at import time; this only covers a remount in dev, where
  // module state survives but the tree is torn down and rebuilt.
  useEffect(() => {
    initPostHog();
  }, []);

  // NOTE: the old cleanup called posthog.reset() here. That threw away the
  // anonymous distinct_id that links pre-signup funnel events to the account
  // they become — and it only ever fired on app teardown or a StrictMode
  // remount. Sign-out still resets, via AuthContext.

  const value = {
    posthog,
    // Helper methods
    capture: (event, properties) => posthog.capture(event, properties),
    identify: (distinctId, properties) =>
      posthog.identify(distinctId, properties),
    reset: () => posthog.reset(),
    setPersonProperties: (properties) =>
      posthog.setPersonProperties(properties),
  };

  return (
    <PostHogContext.Provider value={value}>{children}</PostHogContext.Provider>
  );
};
